import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, refreshAccessToken, setAccessToken, setAuthFailureHandler } from './api';

export interface AdminProfile {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  role: { code: string; name: Record<string, string> };
  permissions: string[];
  mustChangePassword: boolean;
}

interface AuthState {
  admin: AdminProfile | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminProfile | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const reload = useCallback(async () => {
    const { data } = await api.get<AdminProfile>('/admin/auth/me');
    setAdmin(data);
    setStatus('authenticated');
  }, []);

  // При перезагрузке страницы access-токена в памяти нет, но refresh-cookie жива —
  // пробуем восстановить сессию молча.
  useEffect(() => {
    setAuthFailureHandler(() => {
      setAdmin(null);
      setStatus('anonymous');
    });

    void (async () => {
      try {
        await refreshAccessToken();
        await reload();
      } catch {
        setStatus('anonymous');
      }
    })();

    return () => setAuthFailureHandler(null);
  }, [reload]);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<{ admin: AdminProfile; accessToken: string }>(
      '/admin/auth/login',
      { email, password },
    );

    setAccessToken(data.accessToken);
    setAdmin(data.admin);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout');
    } finally {
      setAccessToken(null);
      setAdmin(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      admin,
      status,
      login,
      logout,
      reload,
      can: (permission) => admin?.permissions.includes(permission) ?? false,
    }),
    [admin, status, login, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth используется вне AuthProvider');

  return context;
}
